import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useSearchPages } from "@/app/searchPages";
import { MkIcon } from "@/mk/MkIcon";

import { PmSearchDialog } from "./PmSearchDialog";

/** True when the event comes from something the user is already typing into. */
function isEditable(target: EventTarget | null): boolean {
    const element = target as HTMLElement | null;
    if (!element || typeof element.closest !== "function") {
        return false;
    }
    return Boolean(element.closest(
        'input, textarea, select, [contenteditable="true"], [contenteditable=""], '
        + '[role="textbox"], [role="combobox"]',
    ));
}

/** The dialog and any other open modal would swallow a stray keystroke. */
function dialogOpen(): boolean {
    return Boolean(document.querySelector('[aria-modal="true"], dialog[open]'));
}

export function PmGlobalSearch() {
    const { t } = useTranslation();
    const pages = useSearchPages();
    const [open, setOpen] = useState(false);
    const [seed, setSeed] = useState("");

    const openSearch = useCallback((value = "") => {
        setSeed(value);
        setOpen(true);
    }, []);

    const closeSearch = useCallback(() => setOpen(false), []);

    useEffect(() => {
        if (open) {
            return;
        }
        function onKeyDown(event: KeyboardEvent) {
            if (event.defaultPrevented || event.isComposing) {
                return;
            }
            if (event.ctrlKey || event.metaKey) {
                if (!event.altKey && event.key.toLowerCase() === "k") {
                    event.preventDefault();
                    openSearch();
                }
                return;
            }
            if (event.altKey || isEditable(event.target) || dialogOpen()) {
                return;
            }
            if (event.key === "/") {
                event.preventDefault();
                openSearch();
                return;
            }
            if (event.key.length === 1 && event.key !== " ") {
                event.preventDefault();
                openSearch(event.key);
            }
        }
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [open, openSearch]);

    return (
        <>
            <button type="button"
                    className="pm-search-trigger"
                    aria-label={t("search.title")}
                    aria-haspopup="dialog"
                    aria-expanded={open}
                    title={`${t("search.title")} (Ctrl+K)`}
                    onClick={() => openSearch()}>
                <MkIcon name="fa-search" />
            </button>
            {open && <PmSearchDialog initialTerm={seed} pages={pages} onClose={closeSearch} />}
        </>
    );
}
